// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Body,
  Button,
  H3,
  LgPadding,
  LgSpacing,
  Link,
  MdSpacing,
  Row,
} from '@alto/design-system';
import { useAddToCart, useRemoveFromCart } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import { type NeedlePrescription } from '@alto/scriptdash/alto/patient_app/checkout_flow/v1/checkout_flow_endpoint';
import { PrescriptionStatusMap } from '@alto/scriptdash/alto/patient_app/types/v1/prescription_status';
import { keyBy, uniqBy } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import {
  sendNewYorkFertilityNeedlePromptCompleted,
  sendNewYorkFertilityNeedlePromptViewed,
} from '~shared/actions/checkout';
import NeedleMedsImage from '~shared/assets/images/needle_meds.png';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { prescriptionToItemKey } from '~shared/features/checkout/helpers';
import { getEnterCartData } from '~shared/features/checkout/selectors/getCheckoutFlow';
import { formatListMaxTwo } from '~shared/helpers/list';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { NeedleRowList } from './NeedleRowList';

const addButtonClickedOption = { all_items_button_clicked: false, individual_item_button_clicked: false };

export const parseNeedlePrescription = (prescription: NeedlePrescription) => ({
  id: prescription.id,
  status: PrescriptionStatusMap.READY,
  image_url: prescription.image_url ?? '',
  quantity: prescription.quantity,
  price: prescription.price,
  name: prescription.medication_name,
  family_member_name: '',
  auto_refill_eligible: false,
  manual_coupon_eligible: false,
});

export const AddNeedlesActionSheet = () => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { handleAddToCart } = useAddToCart();
  const { handleRemoveFromCart } = useRemoveFromCart();
  const enterCartData = useSelectorShared(getEnterCartData);
  const { cartItems } = useCartData();

  const needlePrescriptions: NeedlePrescription[] = useMemo(
    () => uniqBy(enterCartData?.needle_prompt?.needle_prescriptions ?? [], 'id'),
    [enterCartData],
  );
  const needlePrescriptionsByID = keyBy(needlePrescriptions, 'id');
  const requireNeedlePrescription = enterCartData?.needle_prompt?.require_needle_prescriptions ?? [];
  const requireMedicationNames = formatListMaxTwo(
    requireNeedlePrescription.map((medication: { medication_name: string }) => medication.medication_name),
  );

  const parsedNeedlePrescriptions = useMemo(
    () => needlePrescriptions.map(parseNeedlePrescription),
    [needlePrescriptions],
  );

  const needlesAddedToCart = cartItems.filter((item) => !!needlePrescriptionsByID[item.resource_id]).length;
  const hasAddedAllNeedles = needlesAddedToCart === needlePrescriptions.length;
  const hasAddedNeedle = needlesAddedToCart > 0;

  const handleAddAllNeedles = () => {
    addButtonClickedOption.all_items_button_clicked = true;
    needlePrescriptions.map(async (prescription: { id: number }) => {
      await handleAddToCart({ resource_id: prescription.id, resource_type: 'Prescription' });
    });
  };

  const handleRemoveAllNeedles = useCallback(() => {
    addButtonClickedOption.all_items_button_clicked = true;
    parsedNeedlePrescriptions.map((prescription) => {
      handleRemoveFromCart({ item: prescriptionToItemKey(prescription.id) });
    });
  }, [handleRemoveFromCart, parsedNeedlePrescriptions]);

  const handleContinue = () => {
    addButtonClickedOption.individual_item_button_clicked =
      hasAddedNeedle && (!addButtonClickedOption.all_items_button_clicked || !hasAddedAllNeedles);
    dispatch(
      sendNewYorkFertilityNeedlePromptCompleted(
        addButtonClickedOption.all_items_button_clicked,
        addButtonClickedOption.individual_item_button_clicked,
      ),
    );
    closeActionSheet();
    navigate('RouteCheckout');
  };

  useEffect(() => {
    dispatch(sendNewYorkFertilityNeedlePromptViewed());
  }, [dispatch, enterCartData]);

  return (
    <ActionSheetV2
      title="Remember to add needles"
      illustrationSrc={NeedleMedsImage}
      illustrationAccessibilityLabel="medicine bottle with needle"
      analyticsName="add_needles"
      buttons={
        hasAddedNeedle
          ? [
              <Button
                key="continue-with-needles"
                label="Continue"
                accessibilityLabel="Continue with added needles"
                onPress={handleContinue}
              />,
            ]
          : [
              <Button
                key="continue-without-needles"
                label="No thanks, I have what I need"
                accessibilityLabel="Continue without adding needles"
                type="secondary"
                onPress={handleContinue}
              />,
            ]
      }
    >
      <LgPadding
        topPadding={SPACING.STATIC.NONE}
        bottomPadding={SPACING.STATIC.NONE}
      >
        <Body color={COLORS.TEXT_COLORS.GREY}>
          {requireMedicationNames?.trim() ?? 'Your medications'} will not come with needles. Be sure to order everything
          you need for your medications.
        </Body>
        <MdSpacing />
        <LgSpacing />
        <Row spaceBetween>
          <H3>Available Needles</H3>
          {!hasAddedAllNeedles && needlePrescriptions.length > 1 && <Link onPress={handleAddAllNeedles}>Add all</Link>}
          {hasAddedAllNeedles && needlePrescriptions.length > 1 && (
            <Link onPress={handleRemoveAllNeedles}>Remove all</Link>
          )}
        </Row>
        <LgSpacing />
        <NeedleRowList prescriptions={parsedNeedlePrescriptions} />
      </LgPadding>
    </ActionSheetV2>
  );
};
